import { Trans } from "@lingui/react/macro";
import Select from "@medoma/components/Select/Select";
import { useLingui } from "@lingui/react/macro";
import { useFormContext } from "react-hook-form";
import {
  type IScheduledShift,
  shiftName,
  shiftTimeDisplayValues,
} from "@medoma/models/shifts";
import { useScheduledShifts } from "@/api/Shifts";
import ErrorMessage from "@medoma/components/ErrorMessage/ErrorMessage";
import { formattedTimeSpan } from "@/components/Time/timeUtils";

export type IShiftPickerFields = {
  shiftId: string;
};

export const ShiftPicker = ({
  date,
  excludeUnassignedShifts,
}: {
  date: Date;
  excludeUnassignedShifts: boolean;
}) => {
  const { t } = useLingui();
  const {
    formState: { errors },
    register,
  } = useFormContext<IShiftPickerFields>();

  const {
    data: scheduledShifts,
    isPending,
    isError,
  } = useScheduledShifts(date, date);

  const sortShifts = (shifts: IScheduledShift[]) => {
    return shifts.sort((a, b) => {
      if (!a.employee) {
        return 1;
      }
      if (!b.employee) {
        return -1;
      }
      if (a.employee && b.employee) {
        return shiftName(
          {
            employee: a.employee,
            competence: a.competence,
          },
          {
            competenceFormat: "full",
            employeeNameFormat: "full",
          },
        ).localeCompare(
          shiftName(
            {
              employee: b.employee,
              competence: b.competence,
            },
            {
              competenceFormat: "full",
              employeeNameFormat: "full",
            },
          ),
        );
      }
      return 0;
    });
  };

  const filterShifts = (shifts: IScheduledShift[]) => {
    if (!excludeUnassignedShifts) {
      return shifts;
    }
    return shifts.filter((shift) => {
      return shift.employee !== null;
    });
  };

  return (
    <>
      {isError ? (
        <ErrorMessage
          message={t`Kunde inte hämta arbetspass. Prova igen om en liten stund och kontakta support om det fortfarande inte fungerar.`}
        />
      ) : (
        <Select
          label={t`Utfört av`}
          {...register(`shiftId`, {
            required: {
              value: true,
              message: t`Välj ett arbetspass`,
            },
          })}
          errorMessage={errors.shiftId?.message}
        >
          {isPending ? (
            <option disabled>
              <Trans>Hämtar arbetspass...</Trans>
            </option>
          ) : (
            <>
              <option value="">
                <Trans>Välj</Trans>
              </option>
              {filterShifts(sortShifts(scheduledShifts)).map(
                (scheduledShift) => {
                  const { id, startDateTime, endDateTime } = scheduledShift;

                  const {
                    startedDayBefore,
                    hasEndTimeAfterToday,
                    formattedDayBefore,
                    formattedDayToday,
                  } = shiftTimeDisplayValues({
                    selectedDate: date,
                    startDateTime,
                    endDateTime,
                  });

                  return (
                    <option key={id} value={id}>
                      {shiftName(scheduledShift, {
                        competenceFormat: "full",
                        employeeNameFormat: "full",
                      })}{" "}
                      (
                      {startedDayBefore
                        ? t`Start ${formattedDayBefore} `
                        : hasEndTimeAfterToday
                          ? t`Start ${formattedDayToday} `
                          : ""}
                      {formattedTimeSpan(startDateTime, endDateTime)})
                    </option>
                  );
                },
              )}
            </>
          )}
        </Select>
      )}
    </>
  );
};
